import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import type { TaskSelect, SessionSelect } from "~/../drizzle/schema";
import { SessionEditDrawer } from "~/components/engagement/sessions/session-edit-drawer";
import {
  useAllEngagementSessions,
  useUpdateSession,
} from "~/lib/queries/sessionQuery";
import type { UpdateSessionInput } from "~/lib/schemas/session";
import type { SessionCardData } from "~/lib/shared/types/session";
import type { ResourceDetailData } from "~/lib/types/resource";

import { CoachSessionTimeline } from "./-components/coach-session-timeline";

type HomeworkWithSessionName = Pick<
  TaskSelect,
  | "id"
  | "title"
  | "status"
  | "deadline"
  | "priority"
  | "description"
  | "engagementId"
> & {
  sessionName: string | null;
  sessionId?: string;
};

export const Route = createFileRoute(
  "/_user/my-coaching/clients/programs/$engagementId/sessions",
)({
  staticData: { title: "Sessions" },
  component: SessionsPage,
});

function SessionsPage() {
  const { engagementId } = Route.useParams();
  const navigate = useNavigate();
  const { data: sessionData } = useAllEngagementSessions(engagementId);

  const [sessionEditDrawerOpen, setSessionEditDrawerOpen] = useState(false);
  // SessionCardData is a subset of SessionSelect; cast is safe since the drawer
  // only reads fields present in SessionCardData (id, title, status, startAt,
  // duration, meetingUrl) plus keySummary (which will be undefined → "").
  const [editingSession, setEditingSession] = useState<SessionSelect | null>(
    null,
  );
  const [editError, setEditError] = useState<string | null>(null);

  const updateSessionMutation = useUpdateSession();

  const handleEditSession = (session: SessionCardData) => {
    setEditingSession(session as unknown as SessionSelect);
    setEditError(null);
    setSessionEditDrawerOpen(true);
  };

  const handleSessionSubmit = (data: UpdateSessionInput) => {
    setEditError(null);
    updateSessionMutation.mutate(data, {
      onSuccess: () => {
        toast.success("Session updated");
        setSessionEditDrawerOpen(false);
        setEditingSession(null);
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update session";
        setEditError(errorMessage);
        toast.error(errorMessage);
      },
    });
  };

  return (
    <>
      <CoachSessionTimeline
        engagementId={engagementId}
        onEditSession={handleEditSession}
      />

      <SessionEditDrawer
        open={sessionEditDrawerOpen}
        homeworkLoading={false}
        onOpenChange={setSessionEditDrawerOpen}
        onSubmit={handleSessionSubmit}
        session={editingSession}
        engagementId={engagementId}
        error={editError}
        onDismissError={() => setEditError(null)}
        availableSessions={sessionData?.sessions ?? []}
        onAddResource={() =>
          navigate({
            to: "/my-coaching/clients/programs/$engagementId/coaching-resources",
            params: { engagementId },
          })
        }
        onEditResource={(_resource: ResourceDetailData) =>
          navigate({
            to: "/my-coaching/clients/programs/$engagementId/coaching-resources",
            params: { engagementId },
          })
        }
        onAddHomework={() =>
          navigate({
            to: "/my-coaching/clients/programs/$engagementId/homework",
            params: { engagementId },
            search: { create: true },
          })
        }
        onEditHomework={(homework: HomeworkWithSessionName) =>
          navigate({
            to: "/my-coaching/clients/programs/$engagementId/homework",
            params: { engagementId },
            search: { editId: homework.id },
          })
        }
      />
    </>
  );
}
